import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

// B.45 P1 — 渐进式启用 5 一级菜单架构。
//
// 一级:产品图库 / 商品管理 / 小程序管理 / 自媒体助手 / 系统设置。
// 实装屏(LibraryView / ImageSearchView / SettingsView)走真 component;
// 其它走 StubView,按 capability + meta 自动渲染"未开通 / 实装中"。
//
// 路由命名约定:`<group>:<screen>`,如 'library:images' / 'business:products'。
// Sidebar.vue 用 route.name 判 active(子串前缀 match)。
const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/library/images' },

  // ─── 产品图库 (Layer 1, 默认启用) ──────────────────────────────
  {
    path: '/library/images',
    name: 'library:images',
    // 屏 02 图片库主页
    component: () => import('@/views/LibraryView.vue')
  },
  {
    path: '/library/search',
    name: 'library:search',
    // 图片搜索(原以图搜图改名扩展):货号/文件名 · 标签 · 以图 三 mode。
    // 以图 mode 由 image_search capability 门控(没买露出带锁,点击弹升级)。
    component: () => import('@/views/ImageSearchView.vue')
  },
  {
    path: '/library/categories',
    name: 'library:categories',
    // 内部主分类管理(product_categories,图库↔商品库共用)。不门控 Business,
    // 纯图库租户也能改名/加/删/排序/显隐;走 ungated /categories CRUD。
    component: () => import('@/views/CategoriesManageView.vue')
  },

  // ─── 商品管理 (Layer 2, capability=business) ───────────────────
  // meta.capability='business':App.vue 的 RouterView 统一拦截 —— 未开通经营版
  // 的租户进来显 UpgradePlaceholder 升级引导,而非渲染视图打 API 吃 403 红条(A1)。
  {
    path: '/business/products',
    name: 'business:products',
    // B.45 P2b 实装(读路径)。写路径/盘点/新增商品 P2c+ 实装。
    component: () => import('@/views/BusinessProductsView.vue'),
    meta: { title: '商品库', capability: 'business' }
  },
  {
    path: '/business/inbound',
    name: 'business:inbound',
    // B.45 P2c 实装 (写路径起步)
    component: () => import('@/views/BusinessInboundView.vue'),
    meta: { title: '进货', capability: 'business' }
  },
  {
    path: '/business/outbound',
    name: 'business:outbound',
    // B.45 P2c.2 实装
    component: () => import('@/views/BusinessOutboundView.vue'),
    meta: { title: '出货', capability: 'business' }
  },
  {
    path: '/business/customers',
    name: 'business:customers',
    // B.45 P2c.3 实装
    component: () => import('@/views/BusinessCustomersView.vue'),
    meta: { title: '客户', capability: 'business' }
  },
  {
    path: '/business/suppliers',
    name: 'business:suppliers',
    // 供应商档案 + 应付核销(docs/plans/supplier-ap.md)
    component: () => import('@/views/BusinessSuppliersView.vue'),
    meta: { title: '供应商', capability: 'business' }
  },
  {
    path: '/business/reports',
    name: 'business:reports',
    // 屏 05 经营报表 (B.45 P3 实装)
    component: () => import('@/views/BusinessReportsView.vue'),
    meta: { title: '经营报表', capability: 'business' }
  },

  // ─── 小程序管理 (Layer 3, capability=miniapp) ──────────────────
  {
    path: '/miniapp/usage',
    name: 'miniapp:usage',
    // 使用说明 — 模块总览/导览，纯静态引导，放小程序管理段首
    component: () => import('@/views/UsageView.vue'),
    meta: { title: '使用说明', capability: 'miniapp' }
  },
  {
    path: '/miniapp/dashboard',
    name: 'miniapp:dashboard',
    // B.46 P8 实装 (聚合 5 数据源 + 装修健康检查)
    component: () => import('@/views/MiniappDashboardView.vue'),
    meta: { title: '运营大盘', capability: 'miniapp' }
  },
  {
    path: '/miniapp/decoration',
    name: 'miniapp:decoration',
    // Phase F.A 实装:商品详情字段开关 + 3 栏装修壳;楼层/底栏留 F.B
    component: () => import('@/views/DecorationView.vue'),
    meta: { title: '店铺管理', capability: 'miniapp', milestone: 'Phase F' }
  },
  // B.51 收口:分类管理并入「店铺管理」→「分类」tab(DecorationView)。
  // 旧 path(分类管理 / 商品上架)保留为重定向,避免书签/内链 404,直接落到分类 tab。
  { path: '/miniapp/categories', redirect: { name: 'miniapp:decoration', query: { tab: 'category' } } },
  { path: '/miniapp/listings',   redirect: { name: 'miniapp:decoration', query: { tab: 'category' } } },
  // "公司介绍" 已并入装修 tab(F.E),路由保留作 backward compat 重定向 → /miniapp/decoration
  {
    path: '/miniapp/company',
    redirect: '/miniapp/decoration'
  },
  {
    path: '/miniapp/visitors',
    name: 'miniapp:visitors',
    // 运营大盘「访客数」下钻:访客列表(轨迹 Lite,谁来过 + 收藏了几款)
    component: () => import('@/views/MiniappVisitorsView.vue'),
    meta: { title: '访客', capability: 'miniapp' }
  },
  {
    path: '/miniapp/favorites',
    name: 'miniapp:favorites',
    // 运营大盘「收藏数」下钻:收藏明细(谁收藏了什么)
    component: () => import('@/views/MiniappFavoritesView.vue'),
    meta: { title: '收藏', capability: 'miniapp' }
  },
  // 客服咨询线索(2026-06-18)退役 → 真实沟通回归微信。旧 path 保留重定向到大盘,避免书签 404。
  { path: '/miniapp/inquiries', redirect: '/miniapp/dashboard' },

  // ─── 自媒体助手 (capability=media_assistant, 一级 flat) ─────────
  // Lite 出图线(docs/plans/media-assistant-lite.md §十一 重设计):**直接进左右两栏编辑器**。
  // 左·对话式取材(图库选图零成本 + 本地上传 + prompt 生图)+ 叠加编辑;右·发布。
  // 模板进顶部下拉、新建/模板进编辑器内弹窗(原首页画廊已折入)。
  {
    path: '/media',
    name: 'media',
    component: () => import('@/views/MediaEditorView.vue'),
    meta: { title: '自媒体助手', capability: 'media_assistant', milestone: 'Phase G' }
  },

  // ─── 系统设置 (always enabled) ──────────────────────────────────
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue')
  },

  // ─── 兜底:旧 path 重定向(避免 bookmark / dev tooling 404)─────
  { path: '/library', redirect: '/library/images' },
  { path: '/search', redirect: '/library/search' }
]

export const router = createRouter({
  history: createWebHistory(),
  routes
})

// desktop-login：不再全屏拦截。bootstrap(令牌恢复身份) 在 AppLayout onMounted 跑;
// 未登录时内容区显 WelcomeGate,登录走侧栏底部登录栏 → LoginModal 弹窗。
